import { Clipboard } from '@angular/cdk/clipboard';
import {
    Component,
    DestroyRef,
    Input,
    signal,
    WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { timer } from 'rxjs';
import { MatButtonAvailableType } from '../../enums/mat-button-types';
import { ConstantsService } from '../../services/constants.service';

/**
 * Component responsible for displaying 'copy to clipboard' button.
 * Specified value is copied to clipboard upon clicking on the button
 */
@Component({
    selector: 'apcam-copy-button-tooltip',
    imports: [MatButtonModule, MatIconModule, MatTooltipModule, TranslatePipe],
    templateUrl: './copy-button-tooltip.component.html',
    styleUrl: './copy-button-tooltip.component.scss',
})
export class CopyButtonTooltipComponent {
    /**
     * Color of Angular Material Button component
     */
    @Input()
    AMColor: 'primary' | 'accent' | 'warn' = 'primary';

    /**
     * Aria label for button
     */
    @Input()
    ariaLabel: string = '';

    /**
     * Class applied to the button
     */
    @Input()
    class: string = '';

    /**
     * Whether the button is disabled
     */
    @Input()
    disabled: boolean = false;

    /**
     * Whether the FAB button is extended
     */
    @Input()
    fabExtended: boolean = false;

    /**
     * Icon name
     */
    @Input()
    iconName: string = 'content_copy';

    /**
     * Button label
     */
    @Input()
    label: string = '';

    /**
     * Type (or variant) of an Angular Material Button
     */
    @Input()
    type: MatButtonAvailableType = MatButtonAvailableType.FLAT;

    /**
     * Value to be copied to clipboard
     */
    @Input()
    value: string = '';

    protected readonly MatButtonAvailableType = MatButtonAvailableType;
    protected tooltipVisible: WritableSignal<boolean> = signal(false);

    constructor(
        private readonly clipboard: Clipboard,
        private readonly destroyRef: DestroyRef
    ) {}

    protected copy(tooltip: MatTooltip): void {
        this.clipboard.copy(this.value);

        this.tooltipVisible.set(true);
        tooltip.show();

        timer(ConstantsService.COPIED_TOOLTIP_HIDE_DELAY)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                tooltip.hide();
                this.tooltipVisible.set(false);
            });
    }
}
